import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback } from 'react';
import Loader from 'react-loader-spinner';
import { useDraft2 } from '../../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { AccountAccessControlConfig } from '../../../api/ApiTypes';
import { Button, Input, remInPx } from '../../../ui';
import { Checkbox } from '../../../ui/Checkbox';
import { InlineHelp } from '../../../ui/InlineHelp';
import { LoadingFrame } from '../../../ui/LoadingGuard';
import { ResultInfo } from '../../../ui/ResultInfo';
import { useLocationScope } from '../../AppLocation';
import { useResolve } from '../../Container';
import { AccountModel } from '../../account/AccountModel';
import { ButtonStrip, Copy, DisableFrame, Flexer, Spacer, Title } from '../../dashboard/elements/LayoutElements';
import { ManageFrame } from './ManageFrame';

const ManageAccountAccessForm = ({ data }: { data: AccountAccessControlConfig }) => {

    const account = useResolve(AccountModel);
    const api = useResolve(ApiClient);
    const qc = useQueryClient();

    const { draft, draftDirty, patchDraft, clearDraftDirty } = useDraft2(data);

    const save = useMutation({
        mutationKey: ['admin.accessControl.save'],
        mutationFn: async (body: AccountAccessControlConfig) => api.exec(v2.admin.accessControl.save(body)),
        onSettled: () => {
            qc.invalidateQueries(['admin.accessControl.info']);
            clearDraftDirty();
        },
    });

    const onSave = useCallback(() => void (save.mutate(draft)), [draft, save]);

    return <Flexer maxWidth="64" padding="1.75 0">
        <Checkbox
            label="Allow users to automatically join teams when a board link is shared with them."
            checked={draft.autoJoinEnabled}
            onChange={x => patchDraft({ autoJoinEnabled: x })}
        />
        {/* <ToggleFrame>
            <Toggle value={draft.autoJoinEnabled} onAction={x => patchDraft({ autoJoinEnabled: x })} />
            <ToggleLabel>Auto-approve anyone with a confirmed email address from a whitelisted domain to join {account.label}?</ToggleLabel>
        </ToggleFrame> */}
        {/* <ToggleHelp>
                    <HelpTip content={restrictHelpText} />
                </ToggleHelp> */}
        <Spacer f={2} />
        <DisableFrame disabled={!draft.autoJoinEnabled}>
            <Input label="Whitelist" placeholder="e.g. *@example.com"
                multiline={{ rows: 5, resize: 'none' }}
                value={draft.autoJoinWhitelist}
                message="The * character acts as a wildcard.  Examples: *@example.com or *@*"
                onChange={x => patchDraft({ autoJoinWhitelist: x })} />
        </DisableFrame>
        <Spacer f={3} />
        <InlineHelp variant="secondary">
            <Title>What does this mean?</Title>
            <Copy>
                Users must be a member of a team in the organization in order to join and participate on a board.
                To streamline access, you can enable automatic joining for users with a confirmed email address from a whitelisted domain.
                If this is disabled, users must be invited to a team with an invite link before they can join a board.
            </Copy>
            <Copy>
                For example, if the whitelist is set to:<br />
                <code>*@example.com</code><br />
                <code>*@sub.example.com</code>
            </Copy>
            <Copy>
                Any user with an email address ending in <code>@example.com</code> or <code>@sub.example.com</code> will be automatically added to the correct
                team when joining a board via a board link.
            </Copy>
        </InlineHelp>
        <Spacer f={2} />
        <ButtonStrip>
            <Button disabled={!draftDirty || save.isLoading} variant="primary" onAction={onSave}>
                {save.isLoading ? 'Saving...' : 'Save Changes'}
                {save.isLoading && <Loader
                    type="TailSpin"
                    color="#fff"
                    height={remInPx(1.2)}
                    width={remInPx(1.2)}
                />}
            </Button>
        </ButtonStrip>
        <Spacer f={2} />
        {save.isSuccess && <ResultInfo mode="positive" message="Access control configuration updated." />}
        {save.isError && <ResultInfo mode="negative" message="Access control configuration failed to update.  Please contact support." />}
    </Flexer >;
};

const useAccessControlQuery = () => {
    const api = useResolve(ApiClient);
    return useQuery({
        queryKey: ['admin.accessControl.info'],
        queryFn: async () => api.exec(v2.admin.accessControl.info()),
    });
};

export const ManageAccountAccessView = () => {

    useLocationScope('access', 'overview');
    const { isLoading, data } = useAccessControlQuery();

    return <ManageFrame title="Access Control">
        {isLoading && <LoadingFrame />}
        {!isLoading && <ManageAccountAccessForm data={data} />}
    </ManageFrame>;
};
